import React, { useState, useEffect, useCallback, useRef } from 'react'
import { searchSessionAudio } from '../../../../../services/SpeechStream'
import { getUserData } from '../../../../../services/API'
import { logSessionInfo } from '../../../../../services/logger'
import AudioSection from './AudioSection'
import studentIssueIcon from '../../../../../../assets/studentPreChecks/student_issue_icon.svg'
import deviceIssueIcon from '../../../../../../assets/studentPreChecks/device_issue_icon.svg'
import './ListenToRecordingView.scss'

export default function ListenToRecordingView(props) {
  const { goToRetry, goToCheckHeadset, goToStudentUseIssue, speechProps, mountPatter } = props
  
  const [audioUrl, setAudioUrl] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [audioError, setAudioError] = useState(false)
  const [showInfoTooltip, setShowInfoTooltip] = useState(false)
  const tooltipRef = useRef(null)
  const infoBtnRef = useRef(null)

  useEffect(() => {
    setupAudio()
    
    // Play mount patter if provided (only on first visit)
    if (mountPatter) {
      speechProps.abortSpeech(() => {
        speechProps.speakWithPromise({ textToSpeak: mountPatter })
      })
    }
  }, [])

  /**
   * Close the info tooltip when clicking outside of it.
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showInfoTooltip &&
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        infoBtnRef.current &&
        !infoBtnRef.current.contains(event.target)
      ) {
        setShowInfoTooltip(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showInfoTooltip])

  const toggleInfoTooltip = () => {
    setShowInfoTooltip(prev => !prev)
  }

  const setupAudio = () => {
    if (!audioUrl && !isLoading) {
      setIsLoading(true)
      setAudioError(false)
      const userData = getUserData()
      console.log("Getting audio URL", userData.activityId)
      
      searchSessionAudio(userData.activityId).then(response => {
        console.log("Audio URL response:", response)
        if (response) {
          setAudioUrl(response)
        } else {
          console.error("No audio URL in response:", response)
          logSessionInfo('teacherTroubleshoot_LISTEN_TO_RECORDING_AUDIO_FAILED', {});
          setAudioError(true)
        }
        setIsLoading(false)
      }).catch(error => {
        console.error("Error getting audio URL:", error)
        logSessionInfo('teacherTroubleshoot_LISTEN_TO_RECORDING_AUDIO_FAILED', {});
        setAudioError(true)
        setIsLoading(false)
      })
    }
  }

  /**
   * Memoized error handler to prevent unnecessary re-renders that cause WaveSurfer
   * to be destroyed and recreated during audio loading.
   */
  const handleAudioError = useCallback(() => {
    setAudioError(true)
  }, [])

  /**
   * Memoized callback for when user clicks play on the audio.
   * Aborts any ongoing speech to prevent interference with the recording playback.
   */
  const handlePlayAudio = useCallback(() => {
    speechProps.abortSpeech()
  }, [speechProps])

  const handleStudentUseIssueClick = () => {
    logSessionInfo('teacherTroubleshoot_STUDENT_USE_ISSUE_CLICKED', {});
    goToStudentUseIssue();
  };

  const handleDeviceMicIssueClick = () => {
    logSessionInfo('teacherTroubleshoot_DEVICE_MIC_ISSUE_CLICKED', {});
    goToCheckHeadset();
  };

  const renderAudioSection = () => {
    return (
      <AudioSection
        audioUrl={audioUrl}
        isLoading={isLoading}
        audioError={audioError}
        handleListenClick={setupAudio}
        handleAudioError={handleAudioError}
        onPlayAudio={handlePlayAudio}
      />
    )
  }

  return (
    <div className="listen-to-recording-view">
      <div className="listen-to-recording-view__content">
        {renderAudioSection()}
        
        <div className="listen-to-recording-view__question-text">
          What do you need help with?
          <button
            ref={infoBtnRef}
            className="listen-to-recording-view__info-btn"
            onClick={toggleInfoTooltip}
            aria-label="Issue category info"
          >
            <span className="listen-to-recording-view__info-icon">i</span>
            {showInfoTooltip && (
              <div ref={tooltipRef} className="listen-to-recording-view__info-tooltip">
                <div className="listen-to-recording-view__info-tooltip-header">Student Use Issues</div>
                <ul className="listen-to-recording-view__info-tooltip-list">
                  <li>Voice is too loud, distorted, peaking, etc.</li>
                  <li>Voice is too soft</li>
                  <li>Student not answering when prompted</li>
                  <li>Too much background noise</li>
                </ul>
                <div className="listen-to-recording-view__info-tooltip-header">Device/Mic Issues</div>
                <ul className="listen-to-recording-view__info-tooltip-list">
                  <li>Static, popping</li>
                  <li>Audio cuts out mid-playback</li>
                  <li>No audio in playback</li>
                </ul>
              </div>
            )}
          </button>
        </div>
        
        <div className="listen-to-recording-view__issue-buttons-container">
          <button 
            className="listen-to-recording-view__issue-button"
            onClick={handleStudentUseIssueClick}
          >
            <div className="listen-to-recording-view__icon-wrapper">
              <img src={studentIssueIcon} alt="Student use issue" />
            </div>
            <span>Student Use Issue</span>
          </button>
          
          <button 
            className="listen-to-recording-view__issue-button"
            onClick={handleDeviceMicIssueClick}
          >
            <div className="listen-to-recording-view__icon-wrapper">
              <img src={deviceIssueIcon} alt="Device/Mic issue" className="device-icon" />
            </div>
            <span>Device/Mic Issue</span>
          </button>
        </div>
      </div>
      
      <button 
        className="listen-to-recording-view__back-button secondary"
        onClick={goToRetry}
      >
        Exit Troubleshoot
      </button>
    </div>
  )
} 